import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useDisconnectAccount, useDisconnectProfile, useSaveTags } from '@/api/hooks/useAccounts'
import { useV1Accounts } from '@/api/hooks/useInstagram'
import { useAuth } from '@/api/hooks/useAuth'
import { useLanguage } from '@/i18n/LanguageContext'
import { AccountCard } from './AccountCard'
import { BulkPhotoModal } from './BulkPhotoModal'
import { EmptyState } from '@/components/shared/EmptyState'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { Account } from '@/types'
import { PermissionBadge } from '@/components/shared/PermissionBadge'
import { Users, Search, Hash, X, LayoutDashboard, Link, Unlink, Instagram, Facebook, ChevronDown, ChevronRight, Settings, XIcon, ShieldCheck, Loader2, CheckCircle, XCircle, RefreshCw, Plus, Check, AlertTriangle, Info, ImageIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface TokenProfileResult {
  profileName: string
  valid: boolean
  addedAt: string
}

interface TokenResult {
  name: string
  igUserId: string
  pageId: string
  fbOnly: boolean
  status: 'valid' | 'invalid' | 'expired'
  expiresAt: string | null
  dataAccessExpiresAt: string | null
  errorMessage: string | null
  errorCategory: 'permission' | 'expired' | 'restricted' | 'rate_limit' | 'network' | 'password' | 'unknown' | null
  errorHint: string | null
  tokenProfiles?: TokenProfileResult[]
}

const errorCategoryConfig: Record<string, { label: string; color: string; bg: string }> = {
  permission: { label: 'Sem Permissao', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
  expired: { label: 'Token Expirado', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
  restricted: { label: 'Restrita', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30' },
  rate_limit: { label: 'Rate Limit', color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30' },
  network: { label: 'Erro de Rede', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
  password: { label: 'Senha Alterada', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10 border-rose-500/30' },
  unknown: { label: 'Erro', color: 'text-muted-foreground', bg: 'bg-muted border-border' },
}

function ProfilePic({ account }: { account: { pageId: string; username?: string; name?: string; fbOnly?: boolean } }) {
  const [error, setError] = useState(false)
  const [bust, setBust] = useState(0)
  const initial = (account.username || account.name || '?').charAt(0).toUpperCase()

  // Recarrega o avatar quando fotos sao trocadas em massa (fura cache do navegador)
  useEffect(() => {
    const h = () => { setBust(Date.now()); setError(false) }
    window.addEventListener('page-photos-updated', h)
    return () => window.removeEventListener('page-photos-updated', h)
  }, [])

  if (error || !account.pageId) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 text-lg font-bold">
        {initial}
      </div>
    )
  }

  return (
    <img
      src={`/api/v2/page-picture/${account.pageId}${bust ? `?v=${bust}` : ''}`}
      alt={account.username || account.name || ''}
      className="h-12 w-12 shrink-0 rounded-xl border-2 border-border object-cover"
      onError={() => setError(true)}
    />
  )
}

function traduzirErro(error: { message: string; category: string }, platform: 'fb' | 'ig'): string {
  const plat = platform === 'fb' ? 'Facebook' : 'Instagram'
  if (error.category === 'permission') {
    if (error.message.includes('administrator') || error.message.includes('moderator')) {
      return `Voce precisa ser administrador, editor ou moderador desta pagina no ${plat}. Verifique suas permissoes.`
    }
    return `Permissoes insuficientes no ${plat}. Reconecte a conta pelo botao "Vincular Conta".`
  }
  if (error.category === 'restricted') {
    return `A pagina exige autenticacao de dois fatores (2FA) no ${plat}. Ative o 2FA nas configuracoes do ${plat}.`
  }
  if (error.category === 'expired') {
    return `Token expirado no ${plat}. Reconecte a conta pelo botao "Vincular Conta".`
  }
  return error.message
}

interface AccountWithTags {
  igUserId: string
  username: string
  name: string
  pageId: string
  fbOnly: boolean
  active: boolean
  fbActive: boolean
  tokenExpiry: string
  tags?: string[]
  steps?: unknown[]
  fbSteps?: unknown[]
  [key: string]: unknown
}

export function Dashboard() {
  const { t, language } = useLanguage()
  const { data: user } = useAuth()
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') || '' : ''
  const returnTo = typeof window !== 'undefined' ? window.location.pathname : '/painel'
  const authUrl = `/auth?token=${encodeURIComponent(authToken)}&returnTo=${encodeURIComponent(returnTo)}`
  const igAuthUrl = `/oauth/instagram/redirect?userId=${user?.id || ''}&returnTo=${encodeURIComponent(returnTo)}`
  const { data: accounts, isLoading } = useV1Accounts()
  const disconnectAccount = useDisconnectAccount()
  const disconnectProfile = useDisconnectProfile()
  const saveTags = useSaveTags()
  const [search, setSearch] = useState('')
  const [activeTags, setActiveTags] = useState<string[]>([])
  const [platformFilter, setPlatformFilter] = useState<'all' | 'ig' | 'fb'>('all')
  const [configAccount, setConfigAccount] = useState<Account | null>(null)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  // Drag-and-drop categorias
  const [dragOverTag, setDragOverTag] = useState<string | null>(null)
  const [draggingAccount, setDraggingAccount] = useState<string | null>(null)
  const [customCategories, setCustomCategories] = useState<string[]>([])
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')

  // Token Checker
  const [tokenResults, setTokenResults] = useState<TokenResult[]>([])
  const [isChecking, setIsChecking] = useState(false)
  const [checkProgress, setCheckProgress] = useState<{ current: number; total: number } | null>(null)
  const [tokenSummary, setTokenSummary] = useState<{ total: number; valid: number; invalid: number } | null>(null)
  const [tokenCheckerOpen, setTokenCheckerOpen] = useState(false)
  const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false)

  const handleCheckTokens = useCallback(async () => {
    setIsChecking(true)
    setTokenResults([])
    setTokenSummary(null)
    setCheckProgress(null)
    setTokenCheckerOpen(true)

    try {
      const token = localStorage.getItem('auth_token')
      const resp = await fetch('/api/accounts/check-tokens', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })

      if (!resp.ok || !resp.body) {
        toast.error(t('Erro ao verificar tokens'))
        setIsChecking(false)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6))
              if (eventType === 'start') {
                setCheckProgress({ current: 0, total: data.total })
              } else if (eventType === 'progress') {
                setCheckProgress({ current: data.current, total: data.total })
                setTokenResults(prev => [...prev, data.account])
              } else if (eventType === 'done') {
                setTokenSummary({ total: data.total, valid: data.valid, invalid: data.invalid })
              }
            } catch { /* ignore */ }
            eventType = ''
          }
        }
      }
    } catch {
      toast.error(t('Erro ao verificar tokens'))
    } finally {
      setIsChecking(false)
    }
  }, [])

  const allTags = useMemo(() => {
    if (!accounts) return []
    const set = new Set<string>()
    accounts.forEach(a => (a.tags || []).forEach(t => set.add(t)))
    return [...set].sort()
  }, [accounts])

  const filtered = useMemo(() => {
    if (!accounts) return []
    return accounts.filter(a => {
      // Platform filter
      if (platformFilter === 'ig' && a.fbOnly) return false
      if (platformFilter === 'fb' && !a.fbOnly) return false

      if (search) {
        const q = search.toLowerCase()
        const matchName = (a.username || '').toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q)
        const matchTag = (a.tags || []).some(t => t.includes(q))
        if (!matchName && !matchTag) return false
      }
      if (activeTags.length > 0) {
        const accountTags = a.tags || []
        if (!activeTags.some(t => accountTags.includes(t))) return false
      }
      return true
    })
  }, [accounts, search, activeTags, platformFilter])

  // Group accounts by first tag
  const grouped = useMemo(() => {
    const groups = new Map<string, typeof filtered>()
    for (const acc of filtered) {
      const tag = (acc.tags && acc.tags.length > 0) ? acc.tags[0] : '__sem_tag__'
      if (!groups.has(tag)) groups.set(tag, [])
      groups.get(tag)!.push(acc)
    }
    // Add custom empty categories
    for (const cat of customCategories) {
      if (!groups.has(cat)) groups.set(cat, [])
    }
    // Sort: tagged groups first (alphabetically), then "sem tag" last
    const entries = [...groups.entries()].sort((a, b) => {
      if (a[0] === '__sem_tag__') return 1
      if (b[0] === '__sem_tag__') return -1
      return a[0].localeCompare(b[0])
    })
    return entries
  }, [filtered, customCategories])

  const toggleTag = (tag: string) => {
    setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])
  }

  const toggleGroup = (tag: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  // --- Auto-scroll during drag ---
  const scrollRafRef = useRef<number>(0)

  useEffect(() => {
    if (!draggingAccount) return

    const EDGE = 80 // px from edge to start scrolling
    const SPEED = 12 // px per frame

    const onDragOver = (e: DragEvent) => {
      const main = document.querySelector('main')
      if (!main) return
      const rect = main.getBoundingClientRect()
      const y = e.clientY

      cancelAnimationFrame(scrollRafRef.current)
      if (y < rect.top + EDGE) {
        const intensity = 1 - (y - rect.top) / EDGE
        scrollRafRef.current = requestAnimationFrame(() => main.scrollBy(0, -SPEED * Math.max(intensity, 0.3)))
      } else if (y > rect.bottom - EDGE) {
        const intensity = 1 - (rect.bottom - y) / EDGE
        scrollRafRef.current = requestAnimationFrame(() => main.scrollBy(0, SPEED * Math.max(intensity, 0.3)))
      }
    }

    document.addEventListener('dragover', onDragOver)
    return () => {
      document.removeEventListener('dragover', onDragOver)
      cancelAnimationFrame(scrollRafRef.current)
    }
  }, [draggingAccount])

  // --- Drag-and-drop handlers ---
  const handleDragStart = (e: React.DragEvent, account: AccountWithTags) => {
    e.dataTransfer.setData('application/x-account-id', account.igUserId)
    e.dataTransfer.effectAllowed = 'move'
    setDraggingAccount(account.igUserId)
  }

  const handleDragEnd = () => {
    setDraggingAccount(null)
    setDragOverTag(null)
  }

  const handleGroupDragOver = (e: React.DragEvent, tag: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragOverTag !== tag) setDragOverTag(tag)
  }

  const handleGroupDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverTag(null)
    }
  }

  const handleGroupDrop = (e: React.DragEvent, targetTag: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverTag(null)
    setDraggingAccount(null)

    const igUserId = e.dataTransfer.getData('application/x-account-id')
    if (!igUserId || !accounts) return

    const account = accounts.find(a => a.igUserId === igUserId)
    if (!account) return

    const currentTags = account.tags || []
    const currentPrimary = currentTags[0] || null

    // No-op if same group
    if (targetTag === '__sem_tag__' && currentTags.length === 0) return
    if (targetTag !== '__sem_tag__' && currentPrimary === targetTag) return

    let newTags: string[]
    if (targetTag === '__sem_tag__') {
      newTags = []
    } else {
      const otherTags = currentTags.filter(t => t !== targetTag)
      newTags = [targetTag, ...otherTags]
    }

    saveTags.mutate({ igUserId, tags: newTags, igLogin: !!(account as Record<string, unknown>).igLogin }, {
      onSuccess: () => {
        toast.success(
          targetTag === '__sem_tag__'
            ? t('Tags removidas')
            : `${t('Movido para')} #${targetTag}`
        )
      },
      onError: () => toast.error(t('Erro ao mover conta')),
    })
  }

  const handleCreateCategory = () => {
    const name = newCategoryName.trim().toLowerCase().replace(/^#/, '').replace(/\s+/g, '-')
    if (!name) return
    if (allTags.includes(name) || customCategories.includes(name)) {
      toast.error(t('Categoria ja existe'))
      return
    }
    setCustomCategories(prev => [...prev, name])
    setNewCategoryName('')
    setShowNewCategory(false)
  }

  const handleDeleteCategory = (tag: string) => {
    // Custom category (no accounts) — just remove from local state
    if (customCategories.includes(tag) && !allTags.includes(tag)) {
      setCustomCategories(prev => prev.filter(c => c !== tag))
      return
    }
    // Real tag with accounts — remove tag from all accounts that have it
    if (!accounts) return
    const affected = accounts.filter(a => (a.tags || []).includes(tag))
    if (affected.length === 0) {
      setCustomCategories(prev => prev.filter(c => c !== tag))
      return
    }
    if (!confirm(`${t('Remover tag')} #${tag} ${t('de')} ${affected.length} ${t(affected.length !== 1 ? 'contas' : 'conta')}?`)) return
    let done = 0
    for (const acc of affected) {
      const newTags = (acc.tags || []).filter(t => t !== tag)
      saveTags.mutate({ igUserId: acc.igUserId, tags: newTags }, {
        onSuccess: () => {
          done++
          if (done === affected.length) {
            toast.success(`Tag #${tag} ${t('removida de')} ${affected.length} ${t(affected.length !== 1 ? 'contas' : 'conta')}`)
          }
        },
        onError: () => toast.error(`${t('Erro ao remover tag de')} ${acc.username || acc.name}`),
      })
    }
    setCustomCategories(prev => prev.filter(c => c !== tag))
    setActiveTags(prev => prev.filter(t => t !== tag))
  }

  const handleDisconnect = (igUserId: string, name: string) => {
    if (!confirm(`${t('Desconectar')} "${name}"? ${t('Os dados de fluxo desta conta serao perdidos.')}`)) return
    disconnectAccount.mutate(igUserId, {
      onSuccess: () => toast.success(`${t('Conta')} "${name}" ${t('desconectada')}`),
      onError: () => toast.error(t('Erro ao desconectar')),
    })
  }

  // Unique profiles across all accounts
  const uniqueProfiles = useMemo(() => {
    if (!accounts) return []
    const profileMap = new Map<string, { name: string; totalPages: number; validPages: number; invalidPages: number }>()
    for (const acc of accounts) {
      for (const token of (acc.tokens ?? [])) {
        const pName = token.profileName || 'Principal'
        const existing = profileMap.get(pName) || { name: pName, totalPages: 0, validPages: 0, invalidPages: 0 }
        existing.totalPages++
        if (token.valid) existing.validPages++
        else existing.invalidPages++
        profileMap.set(pName, existing)
      }
    }
    return Array.from(profileMap.values()).sort((a, b) => b.totalPages - a.totalPages)
  }, [accounts])

  const handleDisconnectProfile = (profileName: string) => {
    if (!confirm(`${t('Desconectar perfil')} "${profileName}"? ${t('Todos os tokens deste perfil serao removidos de todas as paginas. Paginas sem nenhum token restante serao excluidas.')}`)) return
    disconnectProfile.mutate(profileName, {
      onSuccess: (data) => toast.success(`${t('Perfil')} "${profileName}" ${t('desconectado')}: ${data.tokensRemoved} tokens removidos${data.accountsRemoved > 0 ? `, ${data.accountsRemoved} contas removidas` : ''}`),
      onError: () => toast.error(t('Erro ao desconectar perfil')),
    })
  }

  const accountsWithErrors = useMemo(() => {
    if (!accounts) return []
    return accounts.filter(a => a.fbPollingError || a.igPollingError)
  }, [accounts])

  if (isLoading) {
    return (
      <div className="space-y-3 px-6 md:px-8 pb-6">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="h-9 w-full rounded-lg" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      </div>
    )
  }

  const total = accounts?.length ?? 0
  const igCount = accounts?.filter(a => !a.fbOnly).length ?? 0
  const fbCount = accounts?.filter(a => a.fbOnly).length ?? 0

  return (
    <div>
      <PermissionBadge permissions={[
        { name: 'pages_show_list', endpoint: 'GET /me/accounts', description: 'Lists all Facebook Pages managed by the user. Used to populate the connected accounts dashboard.' },
        { name: 'pages_manage_metadata', endpoint: 'POST /{page-id}/subscribed_apps', description: 'Subscribes Pages to webhook events for real-time notifications.' },
      ]} />
      {/* Sticky Header */}
      <div className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <LayoutDashboard className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold tracking-tight">Dashboard</h1>
              <Badge variant="outline" className="ml-1 text-[10px] font-normal text-muted-foreground">
                {total} {t(total !== 1 ? 'contas' : 'conta')}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 ml-[30px]">{t('Gerencie suas contas conectadas')}</p>
          </div>
          <div className="flex items-center gap-2">
            {allTags.length > 0 && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setBulkPhotoOpen(true)}>
                <ImageIcon className="h-3.5 w-3.5" />
                {t('Fotos em massa')}
              </Button>
            )}
            <Button size="sm" variant="outline" asChild>
              <a href={igAuthUrl} className="gap-1.5">
                <Instagram className="h-3.5 w-3.5" />
                {t('Vincular Instagram')}
              </a>
            </Button>
            <Button size="sm" asChild>
              <a href={authUrl} className="gap-1.5">
                <Link className="h-3.5 w-3.5" />
                {t('Vincular Conta')}
              </a>
            </Button>
          </div>
        </div>

        {/* Platform Filter Pills */}
        {total > 0 && (
          <div className="flex gap-1.5 mt-4">
            <button
              type="button"
              onClick={() => setPlatformFilter('all')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-150',
                platformFilter === 'all'
                  ? 'bg-primary/10 border border-primary/20 text-primary'
                  : 'bg-secondary border border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {t('Todos')} ({total})
            </button>
            <button
              type="button"
              onClick={() => setPlatformFilter('ig')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-150',
                platformFilter === 'ig'
                  ? 'bg-primary/10 border border-primary/20 text-primary'
                  : 'bg-secondary border border-border text-muted-foreground hover:text-foreground',
              )}
            >
              <Instagram className="h-3.5 w-3.5" /> Instagram ({igCount})
            </button>
            <button
              type="button"
              onClick={() => setPlatformFilter('fb')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-150',
                platformFilter === 'fb'
                  ? 'bg-primary/10 border border-primary/20 text-primary'
                  : 'bg-secondary border border-border text-muted-foreground hover:text-foreground',
              )}
            >
              <Facebook className="h-3.5 w-3.5" /> Facebook ({fbCount})
            </button>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="space-y-4">

      {language === 'en' && (
        <div className="flex items-start gap-2 border-l-2 border-blue-500 bg-blue-500/5 rounded-lg p-3">
          <Info className="mt-0.5 h-4 w-4 text-blue-500 shrink-0" />
          <p className="text-xs text-muted-foreground">Connected accounts are authorized via Facebook OAuth. Users grant explicit permission for each account linked to this platform.</p>
        </div>
      )}

      {/* Polling Error Alerts */}
      {accountsWithErrors.length > 0 && (
        <div className="border-l-2 border-amber-500 bg-amber-500/5 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            {t('Alertas')} ({accountsWithErrors.length})
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {accountsWithErrors.map(acc => (
              <div key={acc.igUserId || acc.pageId}>
                {acc.fbPollingError?.message && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground pl-6 py-0.5">
                    <Facebook className="h-3 w-3 mt-0.5 shrink-0 text-blue-500" />
                    <span><span className="font-medium text-foreground">{acc.name}</span>: {traduzirErro(acc.fbPollingError, 'fb')}</span>
                  </div>
                )}
                {acc.igPollingError?.message && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground pl-6 py-0.5">
                    <Instagram className="h-3 w-3 mt-0.5 shrink-0 text-pink-500" />
                    <span><span className="font-medium text-foreground">@{acc.username}</span>: {traduzirErro(acc.igPollingError, 'ig')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}


      {total === 0 ? (
        <EmptyState
          icon={Users}
          title={t('Nenhuma conta conectada')}
          description={t('Vincule uma conta do Instagram ou Facebook para comecar a usar as automacoes.')}
          action={{ label: t('Vincular Conta'), href: authUrl }}
        />
      ) : (
        <>
          {/* Connected profiles */}
          {uniqueProfiles.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card/50 px-4 py-2.5">
              <span className="text-[11px] font-medium text-muted-foreground mr-1">{t('Perfis')}:</span>
              {uniqueProfiles.map((profile) => (
                <div
                  key={profile.name}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px]',
                    profile.invalidPages > 0 && profile.validPages === 0
                      ? 'border-destructive/40 bg-destructive/5 text-destructive'
                      : profile.invalidPages > 0
                        ? 'border-yellow-500/40 bg-yellow-500/5 text-yellow-600 dark:text-yellow-400'
                        : 'border-border bg-muted/30 text-foreground',
                  )}
                >
                  <Users className="h-3 w-3" />
                  <span className="font-medium">{profile.name}</span>
                  <span className="text-[9px] opacity-70">({profile.validPages}/{profile.totalPages})</span>
                  <button
                    type="button"
                    onClick={() => handleDisconnectProfile(profile.name)}
                    disabled={disconnectProfile.isPending}
                    className="ml-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    title={t('Desconectar perfil de todas as paginas')}
                  >
                    <Unlink className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Search + tags filter */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('Buscar por nome, @usuario ou #tag...')}
                className="h-9 pl-9 text-sm"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
                <Hash className="h-3 w-3 text-muted-foreground/50" />
                {allTags.map(tag => (
                  <span
                    key={tag}
                    className={cn(
                      'group/tag inline-flex items-center gap-0.5 rounded-full px-2.5 py-0.5 text-[11px] transition-all duration-150 border',
                      activeTags.includes(tag)
                        ? 'bg-primary/10 border-primary/20 text-primary'
                        : 'bg-secondary border-border text-muted-foreground hover:text-foreground',
                      dragOverTag === tag && 'ring-2 ring-primary scale-110',
                    )}
                    onDragOver={(e) => { e.preventDefault(); setDragOverTag(tag) }}
                    onDragLeave={() => setDragOverTag(null)}
                    onDrop={(e) => handleGroupDrop(e, tag)}
                  >
                    <button type="button" onClick={() => toggleTag(tag)}>
                      #{tag}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteCategory(tag) }}
                      className="hidden group-hover/tag:inline-flex items-center justify-center rounded-full p-0.5 -mr-1 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title={`${t('Remover')} #${tag}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                {customCategories.filter(c => !allTags.includes(c)).map(cat => (
                  <span
                    key={cat}
                    className={cn(
                      'group/tag inline-flex items-center gap-0.5 rounded-full border border-dashed border-border px-2.5 py-0.5 text-[11px] text-muted-foreground/50',
                      dragOverTag === cat && 'ring-2 ring-primary scale-110 border-primary text-primary',
                    )}
                    onDragOver={(e) => { e.preventDefault(); setDragOverTag(cat) }}
                    onDragLeave={() => setDragOverTag(null)}
                    onDrop={(e) => handleGroupDrop(e, cat)}
                  >
                    #{cat}
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(cat)}
                      className="hidden group-hover/tag:inline-flex items-center justify-center rounded-full p-0.5 -mr-1 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title={`${t('Remover')} #${cat}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                {activeTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveTags([])}
                    className="text-[10px] text-muted-foreground/60 hover:text-foreground"
                  >
                    {t('limpar')}
                  </button>
                )}

                {(allTags.length > 0 || customCategories.length > 0) && <div className="h-3 w-px bg-border/50 mx-0.5" />}
                {showNewCategory ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleCreateCategory()
                        if (e.key === 'Escape') { setShowNewCategory(false); setNewCategoryName('') }
                      }}
                      placeholder={t('Nome da categoria...')}
                      className="h-5 w-28 text-[10px] px-2"
                      autoFocus
                    />
                    <button type="button" onClick={handleCreateCategory} className="text-primary hover:text-primary/80">
                      <Check className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => { setShowNewCategory(false); setNewCategoryName('') }} className="text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowNewCategory(true)}
                    className="flex items-center gap-0.5 rounded-full border border-dashed border-border px-2 py-0.5 text-[10px] text-muted-foreground/50 transition-colors hover:border-foreground/30 hover:text-foreground"
                  >
                    <Plus className="h-2.5 w-2.5" /> {t('categoria')}
                  </button>
                )}
              </div>
          </div>

          {(search || activeTags.length > 0) && (
            <p className="text-xs text-muted-foreground">
              {filtered.length} {t('de')} {total} {t(total !== 1 ? 'contas' : 'conta')}
            </p>
          )}

          {/* Grouped accounts */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <Search className="mb-2 h-8 w-8 text-muted-foreground/20" />
              <p className="text-xs text-muted-foreground">{t('Nenhuma conta encontrada')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map(([tag, accs]) => {
                const isCollapsed = collapsedGroups.has(tag)
                const showGroupHeader = grouped.length > 1 || tag !== '__sem_tag__'

                return (
                  <div
                    key={tag}
                    onDragOver={(e) => handleGroupDragOver(e, tag)}
                    onDragLeave={handleGroupDragLeave}
                    onDrop={(e) => handleGroupDrop(e, tag)}
                    className={cn(
                      'rounded-lg p-2 -mx-2 transition-all',
                      dragOverTag === tag && (tag === '__sem_tag__'
                        ? 'bg-destructive/5 ring-2 ring-dashed ring-destructive/40'
                        : 'bg-primary/5 ring-2 ring-dashed ring-primary/40'),
                    )}
                  >
                    {showGroupHeader && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(tag)}
                        className={cn(
                          'mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground',
                          dragOverTag === tag && 'text-primary',
                        )}
                      >
                        {isCollapsed
                          ? <ChevronRight className="h-4 w-4" />
                          : <ChevronDown className="h-4 w-4" />
                        }
                        {tag === '__sem_tag__' ? t('Sem tag') : `#${tag}`}
                        <span className="text-xs font-normal text-muted-foreground/60">
                          ({accs.length})
                        </span>
                      </button>
                    )}

                    {!isCollapsed && accs.length === 0 && (
                      <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-border/50 py-8 text-xs text-muted-foreground/50">
                        {t('Arraste contas aqui')}
                      </div>
                    )}

                    {!isCollapsed && accs.length > 0 && (
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {accs.map((account) => {
                          const hasIG = !account.fbOnly
                          const tokenDate = new Date(account.tokenExpiry)
                          const daysLeft = Math.max(0, Math.ceil((tokenDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                          const tokenWarn = daysLeft < 7
                          const isActive = account.active || account.fbActive
                          const tokenCount = account.tokens?.length ?? 0
                          const validTokenCount = account.tokens?.filter(t => t.valid).length ?? 0

                          return (
                            <div
                              key={account.igUserId}
                              draggable
                              onDragStart={(e) => handleDragStart(e, account)}
                              onDragEnd={handleDragEnd}
                              className={cn(
                                'overflow-hidden rounded-xl border border-border bg-card transition-colors duration-150 hover:border-primary/15 cursor-grab active:cursor-grabbing',
                                draggingAccount === account.igUserId && 'opacity-50 scale-95',
                              )}
                            >
                              {/* Card content */}
                              <div className="p-4">
                                {/* Profile row */}
                                <div className="flex items-start gap-3">
                                  <div className="relative">
                                    <ProfilePic account={account} />
                                    <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${isActive ? 'bg-emerald-600 dark:bg-emerald-400' : 'bg-muted-foreground/40'}`} />
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold">
                                      {account.fbOnly ? account.name : `@${account.username}`}
                                    </p>
                                    {!account.fbOnly && account.name && (
                                      <p className="truncate text-[11px] text-muted-foreground">{account.name}</p>
                                    )}

                                    {/* Tags */}
                                    {(account.tags?.length ?? 0) > 0 && (
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {account.tags!.slice(0, 2).map(t => (
                                          <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[9px] text-muted-foreground">#{t}</span>
                                        ))}
                                        {account.tags!.length > 2 && (
                                          <span className="text-[9px] text-muted-foreground/50">+{account.tags!.length - 2}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Status badges */}
                                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                                  {hasIG && (
                                    <Badge variant={account.active ? 'success' : 'secondary'} className="h-5 gap-1 px-1.5 text-[9px]">
                                      <Instagram className="h-2.5 w-2.5" />
                                      {account.active ? 'ON' : 'OFF'}
                                    </Badge>
                                  )}
                                  <Badge
                                    variant={account.fbActive ? 'info' : 'secondary'}
                                    className="h-5 gap-1 px-1.5 text-[9px]"
                                  >
                                    <Facebook className="h-2.5 w-2.5" />
                                    {account.fbActive ? 'ON' : 'OFF'}
                                  </Badge>
                                  {tokenCount > 0 && (
                                    <Badge
                                      variant="outline"
                                      className={`h-5 gap-1 px-1.5 text-[9px] ${
                                        validTokenCount >= 2
                                          ? 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400'
                                          : validTokenCount === 1
                                            ? 'border-yellow-500/50 text-yellow-600 dark:text-yellow-500'
                                            : 'border-destructive/50 text-destructive'
                                      }`}
                                    >
                                      <Users className="h-2.5 w-2.5" />
                                      {validTokenCount}/{tokenCount}
                                    </Badge>
                                  )}
                                  <Badge
                                    variant={tokenWarn ? 'destructive' : 'outline'}
                                    className={`h-5 px-1.5 text-[9px] ${!tokenWarn ? 'text-muted-foreground' : ''}`}
                                  >
                                    {daysLeft}d token
                                  </Badge>
                                  <div className="flex-1" />
                                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/50'}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-600 dark:bg-emerald-400' : 'bg-muted-foreground/30'}`} />
                                    {isActive ? t('Ativo') : t('Inativo')}
                                  </span>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex border-t border-border/50" onDragStart={(e) => { e.preventDefault(); e.stopPropagation() }}>
                                <button
                                  type="button"
                                  onClick={() => setConfigAccount(account)}
                                  className="flex flex-1 items-center justify-center gap-1.5 py-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
                                >
                                  <Settings className="h-3 w-3" />
                                  {t('Configurar')}
                                </button>
                                <div className="w-px bg-border/50" />
                                <button
                                  type="button"
                                  onClick={() => handleDisconnect(account.igUserId, account.fbOnly ? account.name : `@${account.username}`)}
                                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                  title={t('Desconectar conta')}
                                >
                                  <Unlink className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}

              {/* "Sem tag" drop zone when dragging and no __sem_tag__ group */}
              {draggingAccount && !grouped.some(([tag]) => tag === '__sem_tag__') && (
                <div
                  onDragOver={(e) => handleGroupDragOver(e, '__sem_tag__')}
                  onDragLeave={handleGroupDragLeave}
                  onDrop={(e) => handleGroupDrop(e, '__sem_tag__')}
                  className={cn(
                    'rounded-lg p-2 -mx-2 transition-all',
                    dragOverTag === '__sem_tag__' && 'bg-destructive/5 ring-2 ring-dashed ring-destructive/40',
                  )}
                >
                  <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                    {t('Sem tag')}
                  </p>
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-border/50 py-8 text-xs text-muted-foreground/50">
                    {t('Solte aqui para remover tags')}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal de configuracao */}
      <Dialog open={!!configAccount} onOpenChange={(open) => { if (!open) setConfigAccount(null) }}>
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto p-0"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {configAccount && (
            <>
              {/* Header do modal */}
              <div className="flex items-center gap-3 border-b border-border px-5 py-4">
                <ProfilePic account={configAccount} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {configAccount.fbOnly ? configAccount.name : `@${configAccount.username}`}
                  </p>
                  {!configAccount.fbOnly && configAccount.name && (
                    <p className="truncate text-xs text-muted-foreground">{configAccount.name}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {!configAccount.fbOnly && (
                    <Badge variant={configAccount.active ? 'success' : 'secondary'} className="h-5 gap-1 px-1.5 text-[9px]">
                      <Instagram className="h-2.5 w-2.5" />
                      {configAccount.active ? 'ON' : 'OFF'}
                    </Badge>
                  )}
                  <Badge
                    variant={configAccount.fbActive ? 'info' : 'secondary'}
                    className="h-5 gap-1 px-1.5 text-[9px]"
                  >
                    <Facebook className="h-2.5 w-2.5" />
                    {configAccount.fbActive ? 'ON' : 'OFF'}
                  </Badge>
                </div>
                <DialogClose className="ml-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                  <XIcon className="h-4 w-4" />
                </DialogClose>
              </div>
              {/* Conteudo */}
              <AccountCard account={configAccount} allTags={allTags} onClose={() => setConfigAccount(null)} />
            </>
          )}
        </DialogContent>
      </Dialog>

      <BulkPhotoModal open={bulkPhotoOpen} onOpenChange={setBulkPhotoOpen} tags={allTags} accounts={accounts} />

      </div>{/* End Content Area */}
    </div>
  )
}
